import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button, Dropdown, Header, Label } from '@heroui/react'
import { Ambient, LayoutElements, useCanvasScale } from './render'
import { fontStack, usePreloadDesignFonts } from './fonts'
import { PREVIEW_LANGUAGE, fillCalls, sampleBindings } from './sample'
import { previewMenu } from '../lib/kioskMenu'
import type { Service } from '../lib/types'
import { PICKED_FIRST, clamp, elementLabel, elementName, findRoom, fitPicture, newElement, paletteFor, pastThreshold, replaceElement, resizeWithin, sameBox, snap, snapReach, type Guide } from './editing'
import { t, type StaffLanguage } from '../lib/staffText'

/** The preview's language as one the phrase table has. */
const contentLanguage = (l: string): StaffLanguage => (l === 'ru' || l === 'az' ? l : 'en')
import { drawOrder, onScreen, pageIds, textIn, type ElementKind, type LayoutDocument, type LayoutElement, type Screen } from './types'
import { newButton, offeredHere, pageLabel, placeOnScreen } from './pages'
import { serviceName } from '../lib/kioskText'
import { placePlaylist, stillCramped, templateHelps } from './placement'
import { PicturePicker } from './PicturePicker'
import { Notice } from './heroParts'
import { DATE_PARTS, SCREEN_DATE_PARTS } from './dateFormat'
import { addIcon } from './addIcons'
import { GROUP_LABEL, groupOf, searchTools, type ToolGroup } from './toolGroups'
import { ambientOn, canvasPaint, groundInk } from './look'
import { unreachableServices } from '../lib/kioskPages'
import { anchorShift, wheelZoom, type Zoom } from './zoom'
import { moveMany } from './multi'
import { useStaffText } from '../ui/StaffText'
import { TOOL_MIME, carry, centreOn, dropCarried, dropDone, isToolDrag, type Point } from './toolDrag'
import '../styles/studio.css'

/**
 * The canvas: dragging, resizing, snapping, selecting.
 *
 * It draws the layout with the same renderer the screens use and puts a transparent
 * handle layer on top. Nothing in here knows what a "call" is — that is the
 * renderer's business — so a new element kind is added in one place and appears here
 * for free.
 */

// base is the document as the press found it: what undo returns to.
type Drag =
  | { mode: 'move'; id: string; ids: string[]; startX: number; startY: number; originX: number; originY: number; base: LayoutDocument }
  | { mode: 'resize'; id: string; corner: string; startX: number; startY: number; box: LayoutElement; base: LayoutDocument }

/** Live fields the canvas draws as a keyword chip rather than a sample value. */
const KEYWORD_KINDS: ReadonlySet<ElementKind> = new Set<ElementKind>(['branchName', 'deskName', 'deskNumber', 'serviceName', 'clerkName'])

/** The eight handles: the corners resize both ways, the edges one. */
const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const

export function EditorCanvas({
  document: doc,
  screen,
  page,
  selectedIds,
  onSelect,
  onToggle,
  onChange,
  onCommit,
  assetURL,
  branchName,
  services,
  language = PREVIEW_LANGUAGE,
  languages,
  zoom = 'fit',
  onZoom,
  onFitScale,
}: {
  document: LayoutDocument
  screen?: Screen
  /** The kiosk menu page being drawn on. */
  page?: string
  /** What is selected; handles are drawn only when it is one element. */
  selectedIds: readonly string[]
  onSelect: (id: string | null) => void
  /** Shift-click: added to the selection, or taken out. */
  onToggle: (id: string) => void
  /** Called continuously while dragging — cheap, and not recorded for undo. */
  onChange: (doc: LayoutDocument) => void
  /** Called once when the gesture ends, with the document it started from. This is what undo remembers. */
  onCommit: (doc: LayoutDocument, before?: LayoutDocument) => void
  assetURL: (id: string) => string
  branchName: string
  /** The organisation's catalogue, for a kiosk design's service grid. Absent while
   *  it loads and for the other kinds, and then the sample services are drawn. */
  services?: Service[]
  /** Which of the design's languages the preview draws. The sample values follow
   *  it too, so "3 people ahead" reads in the language being checked. */
  language?: string
  /** The design's languages, for the language buttons. */
  languages?: readonly string[]
  /** Fit to the stage, or a fixed scale; scrolled and panned when bigger. */
  zoom?: Zoom
  /** Ctrl/⌘ + wheel asks for a scale, zoomed around the cursor. */
  onZoom?: (scale: number) => void
  /** The scale Fit would draw at, for the zoom control's label. */
  onFitScale?: (scale: number) => void
}) {
  const { lang } = useStaffText()
  const { ref, scale: fitScale } = useCanvasScale(doc.canvas.width, doc.canvas.height)
  const scale = zoom === 'fit' ? fitScale : zoom
  const frameRef = useRef<HTMLDivElement>(null)
  usePreloadDesignFonts(doc)
  useEffect(() => onFitScale?.(fitScale), [fitScale, onFitScale])
  const zoomTo = useRef(onZoom)
  zoomTo.current = onZoom
  const scaleNow = useRef(scale)
  scaleNow.current = scale
  // The canvas point to keep still through a zoom, and where on screen: the cursor
  // for the wheel, else the middle of the stage (kept up to date as it scrolls).
  const anchor = useRef<{ px: number; py: number; cx: number; cy: number } | null>(null)
  const centre = useRef<{ px: number; py: number } | null>(null)
  const gesture = useRef<{ x: number; y: number; px: number; py: number; t: number } | null>(null)
  const noteCentre = () => {
    const node = ref.current
    const frame = frameRef.current?.getBoundingClientRect()
    if (!node || !frame) return
    const box = node.getBoundingClientRect()
    centre.current = {
      px: (box.left + box.width / 2 - frame.left) / scaleNow.current,
      py: (box.top + box.height / 2 - frame.top) / scaleNow.current,
    }
  }
  useEffect(() => {
    const node = ref.current
    if (!node) return
    // A pinch sends sixty events a second: they are added up and applied once a frame.
    let delta = 0
    let at = { x: 0, y: 0 }
    let frameId = 0
    const apply = () => {
      frameId = 0
      const s = scaleNow.current
      const next = wheelZoom(s, delta)
      delta = 0
      const frame = frameRef.current?.getBoundingClientRect()
      // Already at the end of the range: no zoom, so no anchor left waiting for one.
      if (!frame || next === s || !zoomTo.current) return
      // One gesture keeps the design point it began on: measured again each frame,
      // the scroll's whole-pixel rounding would add up into a drift.
      const now = performance.now()
      const g = gesture.current
      if (!g || g.x !== at.x || g.y !== at.y || now - g.t > 400) {
        gesture.current = { x: at.x, y: at.y, px: (at.x - frame.left) / s, py: (at.y - frame.top) / s, t: now }
      }
      const kept = gesture.current!
      kept.t = now
      anchor.current = { px: kept.px, py: kept.py, cx: at.x, cy: at.y }
      zoomTo.current(next)
    }
    const wheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey) || !zoomTo.current) return
      event.preventDefault()
      delta += event.deltaY
      at = { x: event.clientX, y: event.clientY }
      if (!frameId) frameId = requestAnimationFrame(apply)
    }
    node.addEventListener('wheel', wheel, { passive: false })
    node.addEventListener('scroll', noteCentre)
    return () => {
      if (frameId) cancelAnimationFrame(frameId)
      node.removeEventListener('wheel', wheel)
      node.removeEventListener('scroll', noteCentre)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref])
  useLayoutEffect(() => {
    const node = ref.current
    const frame = frameRef.current?.getBoundingClientRect()
    let a = anchor.current
    anchor.current = null
    if (node && frame && !a && centre.current) {
      const box = node.getBoundingClientRect()
      a = { ...centre.current, cx: box.left + box.width / 2, cy: box.top + box.height / 2 }
    } else if (node && !a && zoom !== 'fit') {
      // Opened zoomed: the design in the middle of the room around it.
      node.scrollLeft = (node.scrollWidth - node.clientWidth) / 2
      node.scrollTop = (node.scrollHeight - node.clientHeight) / 2
    }
    if (node && frame && a) {
      node.scrollLeft += anchorShift(frame.left, a.px, scale, a.cx)
      node.scrollTop += anchorShift(frame.top, a.py, scale, a.cy)
    }
    noteCentre()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale])

  // The middle button pans instead of picking.
  const pan = useRef<{ x: number; y: number; left: number; top: number } | null>(null)
  const [drag, setDrag] = useState<Drag | null>(null)
  const [guides, setGuides] = useState<Guide[]>([])
  // A component from the tools panel held over the canvas.
  const [dropping, setDropping] = useState(false)
  const latest = useRef(doc)
  latest.current = doc
  // Whether the pointer has travelled far enough for the press to be a drag.
  const started = useRef(false)

  // preview, so that an element's own sample value is drawn in place of the
  // placeholder it stands for. It is set here and in the gallery and nowhere a
  // customer looks: a hall board, a kiosk and the printer resolve a design against
  // what the visit actually carries.
  //
  // The service grid is drawn from the organisation's own catalogue once it has
  // loaded, so that a designer who chooses and orders services sees their own
  // buttons in their own order — the sample services have ids no design can name.
  // previewMenu is also what the gallery thumbnail and the "start from" tile draw
  // from (Layouts.tsx's previewBindings), so none of the three ever disagrees
  // about what a design's grid shows.
  //
  // Kept to chosen rather than the whole document, unlike those two: this memo
  // is recomputed on every pointer-move of a drag, and a design being dragged has
  // not changed which services it chose.
  const chosen = doc.elements.find((el) => el.kind === 'services')?.options?.serviceIds
  const pages = doc.pages
  // Enough sample calls for the largest call grid, so a column or row added shows at once.
  const callCells = Math.max(0, ...doc.elements.filter((el) => el.kind === 'calls').map((el) => Math.max(el.options?.skip ?? 0, 0) + (el.options?.columns ?? 3) * (el.options?.rows ?? 2)))
  const bindings = useMemo(() => {
    const sample = sampleBindings(branchName, language, languages)
    return {
      ...sample,
      calls: fillCalls(sample.calls ?? [], callCells),
      // Live fields read as their names here; Preview keeps the samples.
      keyword: (kind: ElementKind) => (KEYWORD_KINDS.has(kind) ? elementLabel(kind, contentLanguage(lang)) : undefined),
      services: previewMenu(services, chosen, sample.services ?? [], language, languages?.[0]),
      // A counter's Free screen has a free desk's pill.
      deskPill: screen === 'free' ? ('open' as const) : sample.deskPill,
      assetURL,
      preview: true,
      // A button left to say its service's or page's name says it here too, so
      // a designer never sees an empty button that will not be empty.
      serviceLabel: (id: string) => {
        const s = services?.find((o) => o.id === id)
        return s ? serviceName(s.name, language, s.code, languages?.[0]) : t('designs.page.service', contentLanguage(language))
      },
      pageLabel: (id: string) =>
        textIn(pages?.find((p) => p.id === id)?.name, language, languages?.[0]) || t('designs.ed.page', contentLanguage(language)),
    }
  }, [branchName, assetURL, services, chosen, language, languages, pages, screen, callCells, lang])

  useEffect(() => {
    if (!drag) return

    const move = (event: PointerEvent) => {
      if (!started.current) {
        if (!pastThreshold(event.clientX - drag.startX, event.clientY - drag.startY)) return
        started.current = true
      }
      const current = latest.current
      const element = current.elements.find((el) => el.id === drag.id)
      if (!element) return

      // The pointer moves in screen pixels; the document is in canvas units. One
      // division, here, rather than scattered through the handlers.
      const dx = (event.clientX - drag.startX) / scale
      const dy = (event.clientY - drag.startY) / scale

      if (drag.mode === 'move') {
        // The pressed element snaps; the rest of the selection moves by as much.
        const next = { ...element, x: Math.round(drag.originX + dx), y: Math.round(drag.originY + dy) }
        const others = current.elements.filter((el) => !el.hidden && !drag.ids.includes(el.id) && onScreen(el, screen, page))
        const snapped = snap(next, others, current.canvas, snapReach(scale))
        setGuides(snapped.guides)
        onChange(moveMany(drag.base, drag.ids, snapped.x - drag.originX, snapped.y - drag.originY))
        return
      }
      const next = { ...element, ...resizeWithin(drag.box, drag.corner, dx, dy, current.canvas) }
      onChange(replaceElement(current, clamp(next, current.canvas)))
    }

    const up = () => {
      setDrag(null)
      setGuides([])
      if (!started.current) return
      started.current = false
      const before = drag.base.elements.find((el) => el.id === drag.id)
      const after = latest.current.elements.find((el) => el.id === drag.id)
      if (sameBox(before, after)) onChange(drag.base)
      else onCommit(latest.current, drag.base)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
  }, [drag, scale, screen, page, onChange, onCommit])

  // Arrow keys, Delete and Escape belong to the page (Layouts.tsx), not to this
  // box: they work wherever focus is except in a field, and a second listener here
  // would move the selection twice for one press once the canvas had focus.

  // A hidden element is not drawn and not picked on the canvas, unless the layer list selected it.
  const visible = drawOrder(doc.elements).filter((el) => onScreen(el, screen, page) && (!el.hidden || selectedIds.includes(el.id)))
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null
  const dragged = drag && started.current ? doc.elements.find((el) => el.id === drag.id) : undefined

  return (
    <div
      className="studio-stage__fit"
      ref={ref}
      data-zoom={zoom === 'fit' ? 'fit' : undefined}
      onMouseDown={(event) => event.button === 1 && event.preventDefault()}
      onPointerDownCapture={(event) => {
        if (event.button !== 1) return
        event.preventDefault()
        event.stopPropagation()
        const node = ref.current!
        pan.current = { x: event.clientX, y: event.clientY, left: node.scrollLeft, top: node.scrollTop }
        try {
          node.setPointerCapture(event.pointerId)
        } catch {
          // A pointer the browser no longer tracks: the pan still follows moves over the stage.
        }
      }}
      onPointerMove={(event) => {
        const p = pan.current
        if (!p) return
        ref.current!.scrollLeft = p.left - (event.clientX - p.x)
        ref.current!.scrollTop = p.top - (event.clientY - p.y)
      }}
      onPointerUp={() => (pan.current = null)}
      onPointerCancel={() => (pan.current = null)}
      onDragOver={(event) => {
        if (!isToolDrag(event.dataTransfer.types)) return
        event.preventDefault()
        event.dataTransfer.dropEffect = 'copy'
        if (!dropping) setDropping(true)
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false)
      }}
      onDrop={(event) => {
        if (!isToolDrag(event.dataTransfer.types)) return
        event.preventDefault()
        setDropping(false)
        const frame = frameRef.current?.getBoundingClientRect()
        if (!frame) return dropDone()
        dropCarried({ x: (event.clientX - frame.left) / scale, y: (event.clientY - frame.top) / scale })
      }}
    >
      <div
        ref={frameRef}
        className="layout-frame studio-frame"
        data-dropping={dropping || undefined}
        style={{ width: doc.canvas.width * scale, height: doc.canvas.height * scale }}
      >
        <div
          className="layout-canvas"
          data-theme={doc.canvas.theme ?? undefined}
          data-ink={groundInk(doc.canvas)}
          style={{
            width: doc.canvas.width,
            height: doc.canvas.height,
            transform: `scale(${scale})`,
            background: canvasPaint(doc.canvas, assetURL),
            fontFamily: fontStack(doc.canvas.font),
          }}
        >
          {ambientOn(doc, screen) && <Ambient />}
          <LayoutElements document={doc} bindings={bindings} screen={screen} page={page} />
        </div>

        {/* The handle layer sits outside the scaled canvas and is positioned in
            screen pixels, so a selection outline is the same width however far the
            canvas is zoomed out. An outline scaled with the canvas is invisible on
            a board and four pixels thick on a ticket. */}
        <div className="studio-handles" onPointerDown={() => onSelect(null)}>
          {visible.map((el) => (
            <div
              key={el.id}
              className="studio-box"
              data-selected={selectedIds.includes(el.id) || undefined}
              data-hidden={el.hidden || undefined}
              style={{
                left: el.x * scale,
                top: el.y * scale,
                width: el.w * scale,
                height: el.h * scale,
              }}
              title={elementName(el, doc, lang)}
              onPointerDown={(event) => {
                event.stopPropagation()
                if (event.shiftKey) {
                  onToggle(el.id)
                  return
                }
                // Pressing one of several selected moves them all.
                const group = selectedIds.length > 1 && selectedIds.includes(el.id) ? [...selectedIds] : [el.id]
                if (group.length === 1) onSelect(el.id)
                started.current = false
                setDrag({
                  base: doc,
                  mode: 'move',
                  id: el.id,
                  ids: group,
                  startX: event.clientX,
                  startY: event.clientY,
                  originX: el.x,
                  originY: el.y,
                })
              }}
            >
              {el.id === selectedId &&
                HANDLES.map((corner) => (
                  <span
                    key={corner}
                    className="studio-handle"
                    data-h={corner}
                    onPointerDown={(event) => {
                      event.stopPropagation()
                      started.current = false
                      setDrag({
                        base: doc,
                        mode: 'resize',
                        id: el.id,
                        corner,
                        startX: event.clientX,
                        startY: event.clientY,
                        box: el,
                      })
                    }}
                  />
                ))}
              {/* Where it is while it moves, in the design's own pixels: the
                  numbers the inspector will show once it is let go. */}
              {dragged && dragged.id === el.id && (
                <span className="studio-readout">
                  {drag?.mode === 'resize' ? `${dragged.w} × ${dragged.h}` : `${dragged.x}, ${dragged.y}`}
                </span>
              )}
            </div>
          ))}

          {guides.map((guide, i) => (
            <div
              key={`${guide.axis}-${guide.at}-${i}`}
              className="studio-guide"
              data-axis={guide.axis}
              style={
                guide.axis === 'x'
                  ? { left: guide.at * scale, top: 0, bottom: 0 }
                  : { top: guide.at * scale, left: 0, right: 0 }
              }
            />
          ))}
        </div>
      </div>
    </div>
  )
}

/** A glyph from layout/addIcons, in the button's text colour. */
export function Glyph({ of, size = 18 }: { of: Parameters<typeof addIcon>[0]; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: addIcon(of) }}
    />
  )
}

/** One component in the left panel's list: a line icon and its name. Clicked, or dragged onto the canvas. */
function Tool({ of, label, onClick, onDrop }: { of: Parameters<typeof addIcon>[0]; label: string; onClick: () => void; onDrop?: (at: Point) => void }) {
  return (
    <button
      type="button"
      className="studio-row"
      onClick={onClick}
      draggable={onDrop ? true : undefined}
      onDragStart={
        onDrop
          ? (event) => {
              event.dataTransfer.setData(TOOL_MIME, of)
              event.dataTransfer.effectAllowed = 'copy'
              carry(onDrop)
            }
          : undefined
      }
      onDragEnd={onDrop ? dropDone : undefined}
    >
      <Glyph of={of} size={16} />
      <span className="studio-row__label">{label}</span>
    </button>
  )
}

/**
 * The components of the left panel, grouped and filtered by the search. Everything
 * lands in free room (findRoom) and is selected (Layouts.tsx). On a kiosk menu page
 * a service button is chosen from the services, each marked by whether a customer
 * can reach it already.
 */
export function EditorPalette({
  document: doc,
  modules,
  screen,
  page,
  services,
  query = '',
  onAdd,
}: {
  document: LayoutDocument
  /** The modules the licence includes (identity.modules), which decide whether a
   *  playlist is offered. */
  modules?: readonly string[]
  /** Where a kiosk design is being drawn: buttons go on the menu pages only. */
  screen?: Screen
  page?: string
  services?: Service[]
  /** What the search box holds. */
  query?: string
  onAdd: (element: LayoutElement) => void
}) {
  const { say, lang } = useStaffText()
  // The playlist placed last without room of its own. The hint is kept while that
  // playlist still overlaps something or is too small to read.
  const [crampedId, setCrampedId] = useState<string | null>(null)
  // A picture or a logo is placed only once its file is chosen; at the drop point when dragged.
  const [picking, setPicking] = useState<{ kind: LayoutElement['kind']; at?: Point } | null>(null)
  const add = (kind: LayoutElement['kind'], at?: Point) => {
    if (PICKED_FIRST.includes(kind)) {
      setCrampedId(null)
      setPicking({ kind, at })
      return
    }
    if (kind !== 'media' || doc.kind === 'kiosk') {
      setCrampedId(null)
      const el = place(newElement(kind, doc), at)
      // A playlist on the welcome screen is the attract loop: the whole screen until the first touch.
      onAdd(kind === 'media' && screen === 'attract' ? { ...el, x: 0, y: 0, w: doc.canvas.width, h: doc.canvas.height } : el)
      return
    }
    const { element, cramped } = placePlaylist(doc)
    if (at) {
      setCrampedId(null)
      onAdd(centreOn(element, at, doc.canvas))
      return
    }
    onAdd(element)
    setCrampedId(cramped ? element.id : null)
  }
  const onMenu = doc.kind === 'kiosk' && screen === 'choosing' && page !== undefined
  const pages = pageIds(doc)
  const isMenu = page === pages[0]
  // The first page this one does not open already, for a new page button.
  const target = pages.find((p) => p !== page && p !== pages[0]) ?? pages.find((p) => p !== page)
  // On a menu page, every grid and button is put on that page, so drawing on
  // "Cards" never adds something to the menu as well.
  const place = (el: LayoutElement, at?: Point) => {
    const on = placeOnScreen(el, doc, screen, page)
    return at ? centreOn(on, at, doc.canvas) : findRoom(on, doc)
  }
  const kinds = paletteFor(doc.kind, modules).filter((kind) => offeredHere(doc, kind, screen, page))
  const addButton = (el: LayoutElement, at?: Point) => {
    setCrampedId(null)
    onAdd(at ? centreOn(el, at, doc.canvas) : findRoom(el, doc))
  }
  // Service buttons lead the live data, as the menu's main thing.
  const entries: Array<{ key: string; group: ToolGroup; label: string; node: ReactNode }> = []
  if (onMenu) {
    const label = say('designs.ed.serviceButton')
    entries.push({ key: 'service', group: 'live', label, node: <ServicePick document={doc} page={page!} services={services} onPick={addButton} /> })
    if (target) {
      const pageButton = say('designs.ed.pageButton')
      const addPageButton = (at?: Point) => {
        const el = newButton(doc, page!, { kind: 'page', pageId: target })
        el.content = { az: pageLabel(doc, target, 'az'), en: pageLabel(doc, target, 'en'), ru: pageLabel(doc, target, 'ru') }
        addButton(el, at)
      }
      entries.push({
        key: 'pageButton',
        group: 'live',
        label: pageButton,
        node: (
          <Tool of="page" label={pageButton} onClick={() => addPageButton()} onDrop={addPageButton} />
        ),
      })
    }
    if (!isMenu) {
      const back = say('designs.ed.backButton')
      const addBack = (at?: Point) => addButton(newButton(doc, page!, { kind: 'back' }), at)
      entries.push({ key: 'back', group: 'live', label: back, node: <Tool of="back" label={back} onClick={() => addBack()} onDrop={addBack} /> })
    }
    const booking = say('designs.ed.bookingButton')
    const addBooking = (at?: Point) => addButton(newButton(doc, page!, { kind: 'booking' }), at)
    entries.push({ key: 'booking', group: 'live', label: booking, node: <Tool of="booking" label={booking} onClick={() => addBooking()} onDrop={addBooking} /> })
  }
  for (const kind of kinds) {
    // The date and the time apart: one dateTime each, a screen's naming the month.
    if (kind === 'dateTime') {
      for (const part of doc.kind === 'ticket' ? DATE_PARTS : SCREEN_DATE_PARTS) {
        const label = say(part.label)
        const addPart = (at?: Point) => {
          setCrampedId(null)
          onAdd(place(newElement(kind, doc, { format: part.format }), at))
        }
        entries.push({ key: part.label, group: groupOf(kind), label, node: <Tool of={kind} label={label} onClick={() => addPart()} onDrop={addPart} /> })
      }
      continue
    }
    const label = elementLabel(kind, lang)
    entries.push({ key: kind, group: groupOf(kind), label, node: <Tool of={kind} label={label} onClick={() => add(kind)} onDrop={(at) => add(kind, at)} /> })
  }
  const found = searchTools(entries, query)
  return (
    <>
      {found.length === 0 && <p className="studio-comps__none">{say('designs.tools.none')}</p>}
      {found.map((g) => (
        <div key={g.group} className="studio-comps__group" role="group" aria-label={say(GROUP_LABEL[g.group])}>
          <h3 className="studio-comps__cap">{say(GROUP_LABEL[g.group])}</h3>
          {g.tools.map((t) => (
            <Fragment key={t.key}>{t.node}</Fragment>
          ))}
        </div>
      ))}
      {picking && (
        <PicturePicker
          kind={doc.kind}
          title={picking.kind === 'logo' ? say('designs.el.logo') : undefined}
          onClose={() => setPicking(null)}
          onPick={(asset) => {
            const { kind, at } = picking
            setPicking(null)
            const el = place(newElement(kind, doc), at)
            onAdd(fitPicture({ ...el, assetId: asset.id }, asset.width, asset.height, doc.canvas))
          }}
        />
      )}
      {/* The live region is always there and only its content changes: a region
          mounted together with its text is not announced by every screen reader. */}
      <div role="status">
        {crampedId && stillCramped(doc, crampedId) && (
          <Notice tone="accent" className="studio-tools__note">
            {templateHelps({ ...doc, elements: doc.elements.filter((el) => el.id !== crampedId) })
              ? say('designs.media.noRoom', { template: say('designs.template.splitPlaylist') })
              : say('designs.media.noRoomHere')}
          </Notice>
        )}
      </div>
    </>
  )
}

/**
 * "Service button": the organisation's services to choose from, each marked by
 * whether a customer can already reach it (a button or a grid on a page the menu
 * leads to), those without first. The one way to add a service's button.
 */
function ServicePick({
  document: doc,
  page,
  services,
  onPick,
}: {
  document: LayoutDocument
  page: string
  services?: Service[]
  onPick: (el: LayoutElement) => void
}) {
  const { say, lang } = useStaffText()
  const [open, setOpen] = useState(false)
  const active = (services ?? []).filter((s) => s.active)
  const missing = new Set(unreachableServices(doc, active))
  const listed = [...active.filter((s) => missing.has(s.id)), ...active.filter((s) => !missing.has(s.id))]
  const pick = (serviceId: string) => {
    setOpen(false)
    onPick(newButton(doc, page, { kind: 'service', serviceId }))
  }
  return (
    <Dropdown isOpen={open} onOpenChange={(on) => (on && active.length === 0 ? pick('') : setOpen(on))}>
      <Button
        variant="ghost"
        size="sm"
        className="studio-row"
        aria-label={
          missing.size > 0
            ? `${say('designs.ed.serviceButton')}, ${say('designs.ed.withoutButton', { n: missing.size })}`
            : say('designs.ed.serviceButton')
        }
      >
        <Glyph of="services" size={16} />
        <span className="studio-row__label">{say('designs.ed.serviceButton')}</span>
        {missing.size > 0 && (
          <span className="studio-row__count" title={say('designs.ed.withoutButton', { n: missing.size })} aria-hidden="true">
            {missing.size}
          </span>
        )}
      </Button>
      <Dropdown.Popover placement="right top" className="studio-tool__menu">
        <Dropdown.Menu aria-label={say('designs.page.servicesTitle')} onAction={(key) => pick(String(key))}>
          <Dropdown.Section>
            {missing.size > 0 && <Header className="studio-tool__menunote">{say('designs.ed.withoutButton', { n: missing.size })}</Header>}
            {listed.map((s) => {
              const reached = !missing.has(s.id)
              return (
                <Dropdown.Item key={s.id} id={s.id} textValue={serviceName(s.name, lang as 'az', s.code)}>
                  <span className="studio-services__dot" data-reached={reached || undefined} aria-label={say(reached ? 'designs.ed.hasButton' : 'designs.ed.noButton')} />
                  <Label>{serviceName(s.name, lang as 'az', s.code)}</Label>
                </Dropdown.Item>
              )
            })}
          </Dropdown.Section>
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  )
}
